import Vue from 'vue'

// ─────────────────────────────────────────────────────────────────────────────
// Camera Bypass — end-user app (site-api, group ② in the API contract).
// create → 201 (immediate) | 202 (pending approval); the body always carries
// BOTH camera_bypass and approval_request with one of them null.
// Bypass can only start while the partition is armed → 409 partition_disarmed.
// ─────────────────────────────────────────────────────────────────────────────

function buildQuery (params) {
  const q = Object.keys(params)
    .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map(k => `${k}=${encodeURIComponent(params[k])}`)
  return q.length ? `?${q.join('&')}` : ''
}

// duration (seconds) XOR until_next_disarm; reason_preset_id optional.
function bypassBody ({ duration, untilNextDisarm, reasonPresetId, expectedBypassId }) {
  const data = untilNextDisarm ? { until_next_disarm: true } : { duration }
  if (reasonPresetId !== undefined && reasonPresetId !== null) data.reason_preset_id = reasonPresetId
  if (expectedBypassId !== undefined && expectedBypassId !== null) data.expected_bypass_id = expectedBypassId
  return data
}

export default {
  apiPrefix: '/api/v2/sites',

  // GET /api/v2/sites/{site_id}/camera-bypasses  (appListCameraBypasses)
  // OMIT `include` to get BOTH camera_bypasses (active, "off") AND approval_requests
  // (pending, still armed). The server only runs the sub-queries named in `include`,
  // so include=approval_requests returns camera_bypasses:[] meaning "not asked", NOT
  // "none" — that emptiness is a lie. Pass a CSV subset only to deliberately narrow
  // (e.g. 'camera_bypasses,approval_requests'). (backend guidance 2026-08-21)
  listCameraBypasses: function ({ siteId, deviceId, partitionId, include, limit, offset }) {
    const query = buildQuery({ device_id: deviceId, partition_id: partitionId, include, limit, offset })
    return Vue.http.get(
      Vue.env.apiPath + `${this.apiPrefix}/${siteId}/camera-bypasses${query}`,
      Vue.auth.getBearerHeader()
    )
  },

  // POST /api/v2/sites/{site_id}/cameras/{device_id}/bypass  (appCreateCameraBypass)
  createCameraBypass: function ({ siteId, deviceId, duration, untilNextDisarm, reasonPresetId }) {
    return Vue.http.post(
      Vue.env.apiPath + `${this.apiPrefix}/${siteId}/cameras/${deviceId}/bypass`,
      bypassBody({ duration, untilNextDisarm, reasonPresetId }),
      Vue.auth.getBearerHeader()
    )
  },

  // PUT /api/v2/sites/{site_id}/cameras/{device_id}/bypass  (appEditCameraBypass)
  // Edit = soft-cancel + new insert; the returned camera_bypass.id is NEW.
  editCameraBypass: function ({ siteId, deviceId, duration, untilNextDisarm, expectedBypassId }) {
    return Vue.http.put(
      Vue.env.apiPath + `${this.apiPrefix}/${siteId}/cameras/${deviceId}/bypass`,
      bypassBody({ duration, untilNextDisarm, expectedBypassId }),
      Vue.auth.getBearerHeader()
    )
  },

  // DELETE /api/v2/sites/{site_id}/cameras/{device_id}/bypass  (appClearCameraBypass)
  // expected_bypass_id is optional (optimistic concurrency); sent via request body.
  clearCameraBypass: function ({ siteId, deviceId, expectedBypassId }) {
    const options = Vue.auth.getBearerHeader()
    if (expectedBypassId !== undefined && expectedBypassId !== null) {
      options.body = { expected_bypass_id: expectedBypassId }
    }
    return Vue.http.delete(
      Vue.env.apiPath + `${this.apiPrefix}/${siteId}/cameras/${deviceId}/bypass`,
      options
    )
  },

  // GET /api/v2/sites/{site_id}/partition-arming/affected-bypasses
  // (appGetAffectedBypasses) — preflight: which bypasses a disarm/temp-disarm ends.
  // partitionIds: number[] (omit → whole site).
  getAffectedBypasses: function ({ siteId, partitionIds }) {
    const query = (partitionIds && partitionIds.length)
      ? `?partition_ids=${partitionIds.join(',')}`
      : ''
    return Vue.http.get(
      Vue.env.apiPath + `${this.apiPrefix}/${siteId}/partition-arming/affected-bypasses${query}`,
      Vue.auth.getBearerHeader()
    )
  },
}
